import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { addDays, formatDate, formatTime } from "@/lib/format.js";
import { BannerStack } from "@/views/banner-stack.js";
import { AppointmentDialog } from "@/views/appointment-dialog.js";
import type { Appointment } from "@/lib/types.js";

/**
 * Day list view per REQ-033 (Cron / Notion Calendar pattern). Linear stack,
 * grouped by hour. Used as the horizontal vertical's default.
 */
export function DayListPage(): React.JSX.Element {
  const { api } = useTenant();
  const [day, setDay] = useState(() => new Date());
  const [selected, setSelected] = useState<Appointment | null>(null);
  const dayBounds = useMemo(() => bounds(day), [day]);

  const appointments = useAsync(
    () => api.listAppointments({ from: dayBounds.from, to: dayBounds.to }),
    [api, dayBounds.from, dayBounds.to]
  );
  const services = useAsync(() => api.listServices(), [api]);
  const customers = useAsync(() => api.listCustomers(), [api]);
  const resources = useAsync(() => api.listResources(), [api]);
  const conflicts = useAsync(() => api.listSyncConflicts(), [api]);

  const items = (appointments.data?.data ?? []).slice().sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const serviceById = new Map((services.data?.data ?? []).map((s) => [s.id, s]));
  const customerById = new Map((customers.data?.data ?? []).map((c) => [c.id, c]));
  const resourceById = new Map((resources.data?.data ?? []).map((r) => [r.id, r]));

  useKeyboardShortcuts({
    j: () => setDay((d) => new Date(addDays(d.toISOString(), 1))),
    k: () => setDay((d) => new Date(addDays(d.toISOString(), -1)))
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => setDay((d) => new Date(addDays(d.toISOString(), -1)))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setDay(new Date())}>
          Today
        </Button>
        <Button variant="ghost" size="icon" onClick={() => setDay((d) => new Date(addDays(d.toISOString(), 1)))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="ml-2 text-sm font-medium">{formatDate(day.toISOString())}</span>
      </div>
      {appointments.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nothing today. A clean slate.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((a) => (
            <li
              key={a.id}
              onClick={() => setSelected(a)}
              className="flex cursor-pointer flex-col gap-1 rounded-md border bg-card p-3 text-sm shadow-sm hover:bg-accent"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{formatTime(a.starts_at)}</span>
                  <span className="font-medium">{serviceById.get(a.service_id)?.name ?? "Service"}</span>
                  <Badge variant="outline" className="capitalize">
                    {a.lifecycle_state.replace("_", " ")}
                  </Badge>
                  {/* PRD principal-scoped-reads-and-crm-refs (REQ-PSR-008): prefer
                      the appointment's snapshotted resource_name; fall back to
                      the live resources map for legacy rows that pre-date the
                      column. */}
                  {(a.resource_name ?? resourceById.get(a.primary_resource_id)?.name) && (
                    <Badge
                      variant="outline"
                      data-testid={`day-list-resource-${a.id}`}
                      className="text-muted-foreground"
                    >
                      {a.resource_name ?? resourceById.get(a.primary_resource_id)?.name}
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {a.customer_name ?? customerById.get(a.customer_id)?.name ?? "—"}
                </span>
              </div>
              <BannerStack appointment={a} conflicts={conflicts.data?.data ?? []} />
              {a.notes && <div className="text-xs text-muted-foreground">{a.notes}</div>}
            </li>
          ))}
        </ul>
      )}
      <AppointmentDialog
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
        appointment={selected}
        customers={customers.data?.data ?? []}
        services={services.data?.data ?? []}
        resources={resources.data?.data ?? []}
        conflicts={conflicts.data?.data ?? []}
        onChanged={() => {
          appointments.reload();
          conflicts.reload();
        }}
      />
    </div>
  );
}

function bounds(day: Date): { from: string; to: string } {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(day);
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}
